import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { generateAndCopy } from '../../utils/generatePassword';
import Navbar from '../../components/Navbar/Navbar';
import './ChangePassword.css';

/* ── Icons ──────────────────────────────────────── */
function IconEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function IconDice() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="8.5" cy="15.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="15.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function IconCheckCircle() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/* ── Success Modal ───────────────────────────────── */
function SuccessModal({ onGoToDashboard }) {
  return (
    <>
      <div className="chpw-backdrop" aria-hidden="true" />
      <div className="chpw-modal" role="dialog" aria-modal="true" aria-labelledby="chpw-success-title">
        <div className="chpw-modal-icon">
          <IconCheckCircle />
        </div>
        <h2 id="chpw-success-title" className="chpw-modal-title">
          ¡Contraseña actualizada!
        </h2>
        <p className="chpw-modal-body">
          Tu contraseña ha sido cambiada exitosamente. Ya puedes usar tu nueva contraseña la próxima vez que inicies sesión.
        </p>
        <button className="chpw-modal-btn" onClick={onGoToDashboard}>
          Regresar a Tablero
        </button>
      </div>
    </>
  );
}

/* ── Password field component ───────────────────── */
function PasswordField({ id, name, label, value, onChange, showPassword, onToggleShow, error, autoComplete, onGenerate, clipboardMsg }) {
  return (
    <div className="chpw-form-group">
      <label htmlFor={id} className="chpw-label">{label}</label>
      <div className={`chpw-input-wrapper${error ? ' chpw-input-error' : ''}`}>
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder="••••••••"
          autoComplete={autoComplete}
        />
        {onGenerate && (
          <button
            type="button"
            className="chpw-generate-btn"
            onClick={onGenerate}
            aria-label="Generar contraseña segura"
            title="Generar contraseña segura"
          >
            <IconDice />
          </button>
        )}
        <button
          type="button"
          className="chpw-toggle-btn"
          onClick={onToggleShow}
          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {showPassword ? <IconEyeOff /> : <IconEye />}
        </button>
      </div>
      {error && <span className="chpw-error-msg">{error}</span>}
      {clipboardMsg && <span className="chpw-clipboard-msg">{clipboardMsg}</span>}
    </div>
  );
}

/* ── Main component ─────────────────────────────── */
function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [clipboardMsg, setClipboardMsg] = useState('');

  async function handleGenerate() {
    const pw = await generateAndCopy();
    setForm((prev) => ({ ...prev, next: pw, confirm: pw }));
    setShow((prev) => ({ ...prev, next: true, confirm: true }));
    setErrors((prev) => ({ ...prev, next: '', confirm: '' }));
    setClipboardMsg('Contraseña generada y copiada al portapapeles');
    setTimeout(() => setClipboardMsg(''), 3000);
  }

  function validate() {
    const e = {};
    if (!form.current) {
      e.current = 'La contraseña actual es requerida.';
    }
    if (!form.next) {
      e.next = 'La nueva contraseña es requerida.';
    } else if (form.next.length < 8) {
      e.next = 'La nueva contraseña debe tener al menos 8 caracteres.';
    } else if (form.next === form.current) {
      e.next = 'La nueva contraseña debe ser diferente a la actual.';
    }
    if (!form.confirm) {
      e.confirm = 'Confirma tu nueva contraseña.';
    } else if (form.next && form.confirm !== form.next) {
      e.confirm = 'Las contraseñas no coinciden.';
    }
    return e;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (submitError) setSubmitError('');
  }

  function toggleShow(field) {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Step 1: re-authenticate to verify the current password
      const { data: { user } } = await supabase.auth.getUser();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: form.current,
      });

      if (signInError) {
        setSubmitError('La contraseña actual es incorrecta.');
        setLoading(false);
        return;
      }

      // Step 2: update to the new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: form.next,
      });

      if (updateError) {
        setSubmitError(
          updateError.message?.toLowerCase().includes('same password')
            ? 'La nueva contraseña debe ser diferente a la actual.'
            : 'No se pudo actualizar la contraseña. Intenta de nuevo.'
        );
        return;
      }

      setSuccess(true);
    } catch {
      setSubmitError('Ocurrió un error inesperado. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="chpw-page">
      <Navbar />

      <main className="chpw-main">
        <div className="chpw-card">
          {/* Header */}
          <div className="chpw-card-header">
            <div className="chpw-header-icon">
              <IconShield />
            </div>
            <h1 className="chpw-heading">Cambiar Contraseña</h1>
            <p className="chpw-subheading">
              Ingresa tu contraseña actual y elige una nueva segura.
            </p>
          </div>

          {/* Form */}
          <form className="chpw-form" onSubmit={handleSubmit} noValidate>
            <PasswordField
              id="current"
              name="current"
              label="Contraseña actual"
              value={form.current}
              onChange={handleChange}
              showPassword={show.current}
              onToggleShow={() => toggleShow('current')}
              error={errors.current}
              autoComplete="current-password"
            />
            <PasswordField
              id="next"
              name="next"
              label="Nueva contraseña"
              value={form.next}
              onChange={handleChange}
              showPassword={show.next}
              onToggleShow={() => toggleShow('next')}
              error={errors.next}
              autoComplete="new-password"
              onGenerate={handleGenerate}
              clipboardMsg={clipboardMsg}
            />
            <PasswordField
              id="confirm"
              name="confirm"
              label="Confirmar nueva contraseña"
              value={form.confirm}
              onChange={handleChange}
              showPassword={show.confirm}
              onToggleShow={() => toggleShow('confirm')}
              error={errors.confirm}
              autoComplete="new-password"
            />

            {submitError && (
              <div className="chpw-error-banner" role="alert">
                {submitError}
              </div>
            )}

            <button type="submit" className="chpw-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="chpw-spinner" aria-hidden="true" />
                  Actualizando…
                </>
              ) : (
                'Actualizar contraseña'
              )}
            </button>
          </form>
        </div>
      </main>

      {success && (
        <SuccessModal onGoToDashboard={() => navigate('/dashboard')} />
      )}
    </div>
  );
}

export default ChangePassword;
